// SAB-427 / SAB-575: marketing-site top nav. Logo left, section links center
// (desktop only), auth actions right.
//
// SAB-575 change (extend, don't replace): the single "Sign In / Sign Up" button
// becomes a Log In (ghost) + Sign Up (primary) PAIR, present at BOTH breakpoints.
// The mobile hamburger + its drawer are removed (the "ambiguous floating circular
// icon" from the handoff). Sticky/blur/border shell is preserved.
//
// ⚠️ 44px, not sf-btn-sm (36px): these two are the primary mobile auth entry for a
// native app; the design system mandates a 44px min touch target on mobile.
//
// FLAG (handoff open Q#5 / follow-up): with the hamburger gone, the section links
// (How it works / Sabotage cards / …) are desktop-only and have no mobile home.
// Mobile reaches those sections via the hero's "How it works" button + scrolling.
// A mobile equivalent for the section links is a separate follow-up, not solved here.
import { Link } from 'react-router-dom';
import { trackEvent } from '@/lib/analytics';

const SHOW_FAQ = import.meta.env.VITE_SHOW_FAQ === 'true';
const SHOW_TRUST_LEGALITY = import.meta.env.VITE_SHOW_TRUST_LEGALITY === 'true';

const NAV_LINKS: { href: string; label: string }[] = [
  { href: '#how',   label: 'How it works' },
  { href: '#cards', label: 'Sabotage cards' },
  ...(SHOW_TRUST_LEGALITY ? [{ href: '#trust', label: 'Trust & legality' }] : []),
  ...(SHOW_FAQ ? [{ href: '#faq', label: 'FAQ' }] : []),
];

export function Nav() {
  const trackAuth = (which: 'login' | 'signup') =>
    trackEvent('homepage_auth_click', { source: 'homepage', placement: 'nav', which });

  return (
    <nav
      className="border-b border-border"
      style={{
        background: 'hsl(222 47% 6% / 0.82)',
        backdropFilter: 'blur(18px)',
        WebkitBackdropFilter: 'blur(18px)',
        // iOS safe area: index.html sets viewport-fit=cover, so without this the
        // nav renders under the status bar (SAB-575 missed it). Additive to the
        // nav's existing top padding (0 — height comes from the inner h-14/h-16
        // row), so env() = 0 on web leaves web spacing unchanged.
        paddingTop: 'env(safe-area-inset-top)',
      }}
    >
      <div className="mx-auto max-w-[1180px] px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between gap-4">
        <a href="#top" className="flex items-center shrink-0" aria-label="Sabotage Fantasy home">
          <img src="/images/logo-topbar@2x.png" alt="Sabotage Fantasy" className="h-8 sm:h-[34px] w-auto block" />
        </a>

        {/* Section links — desktop only (hidden below 860px) */}
        <div className="flex items-center gap-[28px] max-[860px]:hidden">
          {NAV_LINKS.map((link) => (
            <a key={link.href} href={link.href}
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
              {link.label}
            </a>
          ))}
        </div>

        {/* Auth actions — BOTH breakpoints. 44px touch targets, radius 10, gap 6px.
            TODO (SAB-575 seam): logged-in state should swap this pair for the wallet
            balance pill + avatar (per the wallet handoff). One component, two states.
            Not wired here (that pill isn't built on this branch) — left as a clean
            seam: gate on useAuth().user and render the pill cluster when present. */}
        <div className="flex items-center gap-1.5 shrink-0">
          <Link
            to="/login"
            onClick={() => trackAuth('login')}
            className="inline-flex h-11 items-center justify-center rounded-[10px] px-4 text-sm font-medium text-foreground hover:bg-white/[0.06] transition-colors"
          >
            Log In
          </Link>
          <Link
            to="/signup"
            onClick={() => trackAuth('signup')}
            className="inline-flex h-11 items-center justify-center rounded-[10px] px-4 text-sm font-medium text-primary-foreground bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary)/0.9)] transition-colors"
          >
            Sign Up
          </Link>
        </div>
      </div>
    </nav>
  );
}

export default Nav;
